/**
 * Fleet Converter
 * 
 * Converts vehicle assignments to the Vehicle format used by the existing fleet system.
 */

import type { FixedRoute } from '../../types/fixedRoutes';
import type { VehicleAssignment, FixedRouteFleetConfig } from './fleetTypes';
import type { Vehicle } from '../../types/vehicle';
import type { OperatingPeriod, BreakPeriod } from '../../types/simulation';
import type { RouteAssignment } from '../../types/fixedRoutes';
import type { Point, Layer } from '../../context/context';
import { parseTime, calculateTimeDifference } from '../../components/map/components/auto-fleet-generator/timeUtils';

/**
 * Convert vehicle assignments to Vehicle objects for fleet creation
 * 
 * When shift allocation is enabled, assignments are already organized into shifts
 * (one VehicleAssignment per shift), and each assignment contains breaks in shiftBreaks.
 * When shift allocation is disabled, each route assignment becomes its own OperatingPeriod.
 * 
 * @param assignments - Array of vehicle assignments (may include shift-break data)
 * @param config - Fleet configuration with vehicle specs
 * @param availablePoints - Array of available points to look up coordinates from
 * @returns Array of Vehicle objects compatible with existing fleet system
 */
export function convertAssignmentsToFleet(
  assignments: VehicleAssignment[],
  config: FixedRouteFleetConfig,
  availablePoints: Point[] = [],
  mapLayers: Layer[] = []
): Vehicle[] {
  // Helper function to get coordinates from a point ID
  // Throws an error if coordinates cannot be found
  // Also checks map layers directly in case point was just created
  const getCoordinatesFromPointId = (pointId: string, locationType: 'start' | 'end'): { lat: number; lng: number } => {
    if (!pointId) {
      throw new Error(`Vehicle ${locationType} location ID is required but was not provided.`);
    }
    
    // First check availablePoints
    let point = availablePoints.find(p => p.id === pointId);
    
    // If not found, check map layers directly (for newly created inferred points)
    if (!point && mapLayers.length > 0) {
      const allPointsFromLayers = mapLayers.flatMap(layer =>
        layer.shapes.filter((s): s is Point => s.type === 'point')
      );
      point = allPointsFromLayers.find(p => p.id === pointId);
    }
    
    if (!point) {
      throw new Error(
        `Vehicle ${locationType} location point with ID "${pointId}" not found. ` +
        `Please ensure the location point exists in the map layers.`
      );
    }
    if (!point.coordinates || point.coordinates.length < 2) {
      throw new Error(
        `Vehicle ${locationType} location point "${point.name || pointId}" has invalid coordinates. ` +
        `Expected [latitude, longitude] array.`
      );
    }
    const lat = point.coordinates[0];
    const lng = point.coordinates[1];
    
    // Validate coordinates are valid numbers
    if (typeof lat !== 'number' || typeof lng !== 'number' || isNaN(lat) || isNaN(lng)) {
      throw new Error(
        `Vehicle ${locationType} location point "${point.name || pointId}" has invalid coordinate values. ` +
        `Latitude: ${lat}, Longitude: ${lng}`
      );
    }
    
    return {
      lat,
      lng
    };
  };

  return assignments.map((assignment) => {
    // Check if this assignment has shift breaks (indicates shift allocation was applied)
    const assignmentWithBreaks = assignment as VehicleAssignment & { 
      shiftBreaks?: Array<{ start: string; end: string; location: string }>;
      onDemandPeriods?: Array<{ 
        start: string; 
        end: string; 
        breaks: BreakPeriod[]; 
        serviceType: 'on-demand' 
      }>;
    };

    // Check if this is a mixed assignment (has both fixed routes and on-demand in same shift)
    const hasFixedRoutes = assignment.routeAssignments.length > 0;
    const hasOnDemand = assignmentWithBreaks.onDemandPeriods && assignmentWithBreaks.onDemandPeriods.length > 0;
    const hasShiftBreaks = assignmentWithBreaks.shiftBreaks && assignmentWithBreaks.shiftBreaks.length > 0;

    if (hasFixedRoutes && hasOnDemand && hasShiftBreaks) {
      // Mixed shift: both fixed routes and on-demand with unified break scheduling
      const routeAssignments: RouteAssignment[] = assignment.routeAssignments.map(
        (routeAssignment) => ({
          routeId: routeAssignment.route.id,
          routeName: routeAssignment.route.name,
          loopNumbers: routeAssignment.loopNumbers,
          startStop: routeAssignment.startStopId,
          endStop: routeAssignment.endStopId,
        })
      );

      // Convert shift breaks to BreakPeriod format
      const breaks: BreakPeriod[] = assignmentWithBreaks.shiftBreaks!.map((breakPeriod) => ({
        start: breakPeriod.start,
        end: breakPeriod.end,
        location: breakPeriod.location || '',
      }));

      // Create a single OperatingPeriod for the mixed shift
      // The shift contains both fixed route assignments and on-demand periods
      // Breaks are scheduled uniformly across the entire shift
      const operatingPeriod: OperatingPeriod = {
        start: assignment.operatingPeriod.startTime,
        end: assignment.operatingPeriod.endTime,
        breaks,
        routeAssignments,
        serviceType: 'mixed' as const, // Mark as mixed service type
      };

      // Note: On-demand periods are stored separately and will be handled by the simulation system
      // The OperatingPeriod contains routeAssignments for fixed routes, and breaks are scheduled
      // uniformly across both service types

      // Get coordinates for start and end locations (required)
      const startCoords = getCoordinatesFromPointId(config.vehicleSpecs.startLocationId, 'start');
      const endCoords = getCoordinatesFromPointId(config.vehicleSpecs.endLocationId, 'end');

      const vehicle: Vehicle = {
        id: assignment.vehicleId,
        name: assignment.vehicleName,
        standardSeats: config.vehicleSpecs.standardSeats,
        wheelchairSeats: config.vehicleSpecs.wheelchairSeats,
        startLocation: config.vehicleSpecs.startLocationId,
        endLocation: config.vehicleSpecs.endLocationId,
        operatingHours: [operatingPeriod],
        numberOfVehicles: 1,
        startLocationCoordinates: startCoords,
        endLocationCoordinates: endCoords,
      };

      return vehicle;
    }

    // Check if this is a mixed assignment without shift breaks (has both fixed routes and on-demand)
    if (hasFixedRoutes && hasOnDemand && !hasShiftBreaks) {
      // Mixed assignment without shift allocation: create separate OperatingPeriods for each service type
      const operatingPeriods: OperatingPeriod[] = [];
      
      // Add fixed route periods
      assignment.routeAssignments.forEach((routeAssignment) => {
        const routeAssignments: RouteAssignment[] = [
          {
            routeId: routeAssignment.route.id,
            routeName: routeAssignment.route.name,
            loopNumbers: routeAssignment.loopNumbers,
            startStop: routeAssignment.startStopId,
            endStop: routeAssignment.endStopId,
          },
        ];
        
        operatingPeriods.push({
          start: routeAssignment.startTime,
          end: routeAssignment.endTime,
          breaks: [],
          routeAssignments,
          serviceType: 'fixed' as const,
        });
      });
      
      // Add on-demand periods
      assignmentWithBreaks.onDemandPeriods!.forEach((period) => {
        operatingPeriods.push({
          start: period.start,
          end: period.end,
          breaks: period.breaks || [],
          routeAssignments: [],
          serviceType: 'on-demand' as const,
        });
      });
      
      // Sort by start time
      operatingPeriods.sort((a, b) => parseTime(a.start) - parseTime(b.start));
      
      // Get coordinates for start and end locations (required)
      const startCoords = getCoordinatesFromPointId(config.vehicleSpecs.startLocationId, 'start');
      const endCoords = getCoordinatesFromPointId(config.vehicleSpecs.endLocationId, 'end');
      
      const vehicle: Vehicle = {
        id: assignment.vehicleId,
        name: assignment.vehicleName,
        standardSeats: config.vehicleSpecs.standardSeats,
        wheelchairSeats: config.vehicleSpecs.wheelchairSeats,
        startLocation: config.vehicleSpecs.startLocationId,
        endLocation: config.vehicleSpecs.endLocationId,
        operatingHours: operatingPeriods,
        numberOfVehicles: 1,
        startLocationCoordinates: startCoords,
        endLocationCoordinates: endCoords,
      };

      return vehicle;
    }

    // Check if this is an on-demand assignment (has onDemandPeriods)
    if (assignmentWithBreaks.onDemandPeriods && assignmentWithBreaks.onDemandPeriods.length > 0 && !hasFixedRoutes) {
      // On-demand assignment: convert onDemandPeriods to OperatingPeriods
      // If shift breaks are present, use them; otherwise use breaks from individual periods
      const shiftBreaks = assignmentWithBreaks.shiftBreaks;
      
      if (shiftBreaks && shiftBreaks.length > 0) {
        // Unified break scheduling: one OperatingPeriod per shift with breaks
        const breaks: BreakPeriod[] = shiftBreaks.map((breakPeriod) => ({
          start: breakPeriod.start,
          end: breakPeriod.end,
          location: breakPeriod.location || '',
        }));

        const operatingPeriod: OperatingPeriod = {
          start: assignment.operatingPeriod.startTime,
          end: assignment.operatingPeriod.endTime,
          breaks,
          routeAssignments: [], // Empty for on-demand
          serviceType: 'on-demand' as const,
        };

        // Get coordinates for start and end locations
        const startCoords = getCoordinatesFromPointId(config.vehicleSpecs.startLocationId, 'start');
        const endCoords = getCoordinatesFromPointId(config.vehicleSpecs.endLocationId, 'end');

        const vehicle: Vehicle = {
          id: assignment.vehicleId,
          name: assignment.vehicleName,
          standardSeats: config.vehicleSpecs.standardSeats,
          wheelchairSeats: config.vehicleSpecs.wheelchairSeats,
          startLocation: config.vehicleSpecs.startLocationId,
          endLocation: config.vehicleSpecs.endLocationId,
          operatingHours: [operatingPeriod],
          numberOfVehicles: 1,
          startLocationCoordinates: startCoords,
          endLocationCoordinates: endCoords,
        };

        return vehicle;
      } else {
        // No shift breaks: create one OperatingPeriod per on-demand period
        const operatingPeriods: OperatingPeriod[] = assignmentWithBreaks.onDemandPeriods.map((period) => ({
          start: period.start,
          end: period.end,
          breaks: period.breaks || [],
          routeAssignments: [], // Empty for on-demand
          serviceType: 'on-demand' as const,
        }));

        // Get coordinates for start and end locations
        const startCoords = getCoordinatesFromPointId(config.vehicleSpecs.startLocationId, 'start');
        const endCoords = getCoordinatesFromPointId(config.vehicleSpecs.endLocationId, 'end');

        const vehicle: Vehicle = {
          id: assignment.vehicleId,
          name: assignment.vehicleName,
          standardSeats: config.vehicleSpecs.standardSeats,
          wheelchairSeats: config.vehicleSpecs.wheelchairSeats,
          startLocation: config.vehicleSpecs.startLocationId,
          endLocation: config.vehicleSpecs.endLocationId,
          operatingHours: operatingPeriods,
          numberOfVehicles: 1,
          startLocationCoordinates: startCoords,
          endLocationCoordinates: endCoords,
        };

        return vehicle;
      }
    }

    // If shift allocation was applied, consolidate route assignments into a single shift (OperatingPeriod)
    if (assignmentWithBreaks.shiftBreaks && assignmentWithBreaks.shiftBreaks.length > 0) {
      // Shift allocation: one OperatingPeriod per shift with all route assignments and breaks
      const routeAssignments: RouteAssignment[] = assignment.routeAssignments.map(
        (routeAssignment) => ({
          routeId: routeAssignment.route.id,
          routeName: routeAssignment.route.name,
          loopNumbers: routeAssignment.loopNumbers,
          startStop: routeAssignment.startStopId,
          endStop: routeAssignment.endStopId,
        })
      );

      // Convert shift breaks to BreakPeriod format
      const breaks: BreakPeriod[] = assignmentWithBreaks.shiftBreaks.map((breakPeriod) => ({
        start: breakPeriod.start,
        end: breakPeriod.end,
        location: breakPeriod.location || '',
      }));
      
      // Debug: Log breaks being added to OperatingPeriod
      if (breaks.length > 0) {
        console.log(
          `[Fleet Converter] Vehicle ${assignment.vehicleName}: ` +
          `Adding ${breaks.length} break(s) to OperatingPeriod:`,
          breaks.map(b => `${b.start}-${b.end}`).join(', ')
        );
      }

      // Use the shift's operating period (already calculated by shift allocator)
      const operatingPeriod: OperatingPeriod = {
        start: assignment.operatingPeriod.startTime,
        end: assignment.operatingPeriod.endTime,
        breaks,
        routeAssignments,
        serviceType: 'fixed' as const,
      };

      // Warn if route assignments don't fill entire shift (short shifts) - 5.3.4
      const shiftStart = parseTime(assignment.operatingPeriod.startTime);
      const shiftEnd = parseTime(assignment.operatingPeriod.endTime);
      const shiftDuration = calculateTimeDifference(shiftStart, shiftEnd);
      
      // Calculate total route assignment duration
      let totalRouteDuration = 0;
      assignment.routeAssignments.forEach((routeAssignment) => {
        const routeStart = parseTime(routeAssignment.startTime);
        const routeEnd = parseTime(routeAssignment.endTime);
        totalRouteDuration += calculateTimeDifference(routeStart, routeEnd);
      });
      
      // Calculate total break duration
      let totalBreakDuration = 0;
      breaks.forEach((breakPeriod) => {
        const breakStart = parseTime(breakPeriod.start);
        const breakEnd = parseTime(breakPeriod.end);
        totalBreakDuration += calculateTimeDifference(breakStart, breakEnd);
      });
      
      // If route assignments + breaks are less than 50% of shift duration, warn
      const utilizationPercent = shiftDuration > 0 
        ? ((totalRouteDuration + totalBreakDuration) / shiftDuration) * 100 
        : 0;
      
      if (utilizationPercent < 50 && shiftDuration > 60) {
        // Only warn for shifts longer than 1 hour with low utilization
        console.warn(
          `Short shift detected for vehicle ${assignment.vehicleName}: ` +
          `Shift duration: ${shiftDuration} minutes, ` +
          `Route assignments: ${totalRouteDuration} minutes, ` +
          `Breaks: ${totalBreakDuration} minutes, ` +
          `Utilization: ${utilizationPercent.toFixed(1)}%`
        );
      }

      // Get coordinates for start and end locations (required)
      const startCoords = getCoordinatesFromPointId(config.vehicleSpecs.startLocationId, 'start');
      const endCoords = getCoordinatesFromPointId(config.vehicleSpecs.endLocationId, 'end');

      const vehicle: Vehicle = {
        id: assignment.vehicleId,
        name: assignment.vehicleName,
        standardSeats: config.vehicleSpecs.standardSeats,
        wheelchairSeats: config.vehicleSpecs.wheelchairSeats,
        startLocation: config.vehicleSpecs.startLocationId,
        endLocation: config.vehicleSpecs.endLocationId,
        operatingHours: [operatingPeriod],
        numberOfVehicles: 1,
        startLocationCoordinates: startCoords,
        endLocationCoordinates: endCoords,
      };

      return vehicle;
    } else {
      // Phase 1A behavior: one OperatingPeriod per route assignment (no shift allocation)
      const operatingPeriods: OperatingPeriod[] = assignment.routeAssignments.map(
        (routeAssignment) => {
          // Convert route assignment to RouteAssignment format for OperatingPeriod
          const routeAssignments: RouteAssignment[] = [
            {
              routeId: routeAssignment.route.id,
              routeName: routeAssignment.route.name,
              loopNumbers: routeAssignment.loopNumbers,
              startStop: routeAssignment.startStopId,
              endStop: routeAssignment.endStopId,
            },
          ];

          const periodStart = routeAssignment.startTime;
          const periodEnd = routeAssignment.endTime;

          // Validate that times are valid (should always be true, but safety check)
          if (!periodStart || !periodEnd) {
            console.warn('Invalid route assignment times:', routeAssignment);
            // Fallback to route's operating hours
            return {
              start: routeAssignment.route.startTime,
              end: routeAssignment.route.endTime || '22:00',
              breaks: [],
              routeAssignments,
              serviceType: 'fixed' as const,
            };
          }

          return {
            start: periodStart,
            end: periodEnd,
            breaks: [],
            routeAssignments,
            serviceType: 'fixed' as const,
          };
        }
      );

      // Get coordinates for start and end locations (required)
      const startCoords = getCoordinatesFromPointId(config.vehicleSpecs.startLocationId, 'start');
      const endCoords = getCoordinatesFromPointId(config.vehicleSpecs.endLocationId, 'end');

      const vehicle: Vehicle = {
        id: assignment.vehicleId,
        name: assignment.vehicleName,
        standardSeats: config.vehicleSpecs.standardSeats,
        wheelchairSeats: config.vehicleSpecs.wheelchairSeats,
        startLocation: config.vehicleSpecs.startLocationId,
        endLocation: config.vehicleSpecs.endLocationId,
        operatingHours: operatingPeriods,
        numberOfVehicles: 1,
        startLocationCoordinates: startCoords,
        endLocationCoordinates: endCoords,
      };

      return vehicle;
    }
  });
}

